/* eslint-disable @typescript-eslint/no-explicit-any */
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { blackText, secondary, straightFont, white } from '../../../Constants';
import GenericDialog from '../../GenericDialog';
import { useAuth } from '../../../contexts/AuthContext';

// Shared text field styling
const textFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontFamily: straightFont,
    '& fieldset': {
      borderColor: alpha(blackText, 0.2),
    },
    '&:hover fieldset': {
      borderColor: alpha(blackText, 0.4),
    },
    '&.Mui-focused fieldset': {
      borderColor: secondary,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: straightFont,
    color: alpha(blackText, 0.6),
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: secondary,
  },
};

interface IForgotPasswordProps {
  showForgotPassword: boolean;
  setShowForgotPassword: Dispatch<SetStateAction<boolean>>;
  setShowSignup: Dispatch<SetStateAction<boolean>>;
  setShowLogin: Dispatch<SetStateAction<boolean>>;
}

export default function ForgotPassword(props: IForgotPasswordProps) {
  const { showForgotPassword, setShowForgotPassword, setShowSignup, setShowLogin } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const { resetPassword } = useAuth();

  useEffect(() => {
    if (showForgotPassword) {
      setError('');
      setMessage('');
    }
  }, [showForgotPassword]);

  const closeModal = () => {
    setShowForgotPassword(false);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setMessage('');
      setLoading(true);
      await resetPassword(email);
      setMessage('Check your inbox for further instructions');
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleSignup = () => {
    closeModal();
    setShowSignup(true);
  };

  const handleLogin = () => {
    closeModal();
    setShowLogin(true);
  };

  return (
    <GenericDialog open={showForgotPassword} closeModal={closeModal} isLoading={loading}>
      <Box
        component="form"
        sx={{
          p: 4,
          maxWidth: 400,
          backgroundColor: white,
        }}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Header */}
        <Typography
          variant="h5"
          sx={{
            fontFamily: straightFont,
            fontWeight: 600,
            color: blackText,
            textAlign: 'center',
            mb: 1,
          }}
        >
          Reset Password
        </Typography>
        <Typography
          sx={{
            fontFamily: straightFont,
            fontSize: '0.9rem',
            color: alpha(blackText, 0.6),
            textAlign: 'center',
            mb: 3,
          }}
        >
          Enter your email and we&apos;ll send you a reset link
        </Typography>

        {/* Form Fields */}
        <TextField
          type="email"
          label="Email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={textFieldSx}
          variant="outlined"
          size="medium"
          fullWidth
        />

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}

        {/* Buttons */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={closeModal}
            fullWidth
            sx={{
              color: alpha(blackText, 0.7),
              borderColor: alpha(blackText, 0.2),
              fontFamily: straightFont,
              fontWeight: 500,
              py: 1.25,
              textTransform: 'none',
              '&:hover': {
                borderColor: alpha(blackText, 0.4),
                backgroundColor: alpha(blackText, 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            onClick={handleSubmit}
            fullWidth
            sx={{
              backgroundColor: secondary,
              fontFamily: straightFont,
              fontWeight: 600,
              py: 1.25,
              textTransform: 'none',
              boxShadow: `0 4px 14px ${alpha(secondary, 0.35)}`,
              '&:hover': {
                backgroundColor: alpha(secondary, 0.9),
                boxShadow: `0 6px 20px ${alpha(secondary, 0.45)}`,
              },
            }}
          >
            Send Reset Link
          </Button>
        </Stack>

        {/* Footer Links */}
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(blackText, 0.1)}` }}
        >
          <Button
            variant="text"
            onClick={handleLogin}
            sx={{
              fontFamily: straightFont,
              fontSize: '0.875rem',
              color: secondary,
              textTransform: 'none',
              '&:hover': { backgroundColor: alpha(secondary, 0.08) },
            }}
          >
            Back to Login
          </Button>
          <Typography sx={{ color: alpha(blackText, 0.3) }}>|</Typography>
          <Button
            variant="text"
            onClick={handleSignup}
            sx={{
              fontFamily: straightFont,
              fontSize: '0.875rem',
              color: alpha(blackText, 0.6),
              textTransform: 'none',
              '&:hover': { backgroundColor: alpha(blackText, 0.04) },
            }}
          >
            Create account
          </Button>
        </Stack>
      </Box>
    </GenericDialog>
  );
}
